import type { CompanyId } from "@repo/app-types/entities/company";

/**
 * Word lists for the privacy-curtain workspace aliases, e.g. "Cedar Fox".
 * Do not reorder, remove, or append entries: the alias is derived from a hash
 * of the company id modulo the list lengths, so any change to order or length
 * reshuffles the alias every customer already has.
 */
const ALIAS_FIRST_WORDS = [
  "Cedar",
  "Silver",
  "Amber",
  "Copper",
  "Ivory",
  "Crimson",
  "Golden",
  "Slate",
  "Coral",
  "Indigo",
  "Maple",
  "Onyx",
  "Pearl",
  "Ruby",
  "Sable",
  "Teal",
  "Umber",
  "Violet",
  "Willow",
  "Aspen",
  "Birch",
  "Cobalt",
  "Dusty",
  "Ember",
  "Frost",
  "Hazel",
  "Jade",
  "Lunar",
  "Misty",
  "Noble",
  "Ochre",
  "Quiet",
] as const;

const ALIAS_SECOND_WORDS = [
  "Fox",
  "Pine",
  "Owl",
  "Bear",
  "Wolf",
  "Hawk",
  "Deer",
  "Crane",
  "Otter",
  "Lynx",
  "Heron",
  "Falcon",
  "Badger",
  "Raven",
  "Elk",
  "Finch",
  "Marten",
  "Osprey",
  "Puffin",
  "Robin",
  "Seal",
  "Swift",
  "Tern",
  "Vole",
  "Wren",
  "Yak",
  "Ash",
  "Brook",
  "Cliff",
  "Dune",
  "Fern",
  "Grove",
] as const;

/** FNV-1a 32-bit hash — deterministic across sessions and browsers. */
function fnv1aHash(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts to stay within Number precision.
    hash =
      (hash +
        ((hash << 1) +
          (hash << 4) +
          (hash << 7) +
          (hash << 8) +
          (hash << 24))) >>>
      0;
  }
  return hash;
}

/**
 * Deterministic, memorable alias for a customer workspace, safe to show while
 * screen sharing. The same company id always maps to the same alias.
 */
export function getWorkspaceAlias(companyId: CompanyId): string {
  const hash = fnv1aHash(companyId);
  const firstIndex = hash % ALIAS_FIRST_WORDS.length;
  const secondIndex =
    Math.floor(hash / ALIAS_FIRST_WORDS.length) % ALIAS_SECOND_WORDS.length;
  const first = ALIAS_FIRST_WORDS[firstIndex] ?? ALIAS_FIRST_WORDS[0];
  const second = ALIAS_SECOND_WORDS[secondIndex] ?? ALIAS_SECOND_WORDS[0];
  return `${first} ${second}`;
}
